import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";

export function SectionHeading({ overline, title, subtitle, center = true }) {
  return (
    <div className={`${center ? "text-center max-w-2xl mx-auto" : ""} mb-10 lg:mb-14`}>
      {overline && <p className="overline mb-3">{overline}</p>}
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-espresso leading-tight">{title}</h2>
      {subtitle && <p className="text-espresso/60 mt-4 text-sm sm:text-base leading-relaxed font-sans">{subtitle}</p>}
    </div>
  );
}

function pageNumbers(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}

export default function ProductGrid({ products = [], loading, cols = 4, paginate = false, pageSize = 12 }) {
  const [page, setPage] = useState(1);
  const topRef = useRef(null);
  const totalPages = Math.max(1, Math.ceil(products.length / pageSize));

  useEffect(() => { setPage(1); }, [products]);

  const gridCls = cols === 3
    ? "grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-8 lg:gap-x-6 lg:gap-y-12"
    : "grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-6 lg:gap-y-12";

  if (loading) {
    return (
      <div className={gridCls}>
        {Array.from({ length: cols === 3 ? 6 : 8 }).map((_, i) => (
          <div key={`skeleton-${i}`} className="animate-pulse">
            <div className="aspect-[3/4] bg-beige rounded-2xl" />
            <div className="h-4 bg-beige rounded mt-3 w-3/4" />
            <div className="h-3 bg-beige rounded mt-2 w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  if (!products.length) {
    return <p className="text-center text-espresso/50 py-16 font-serif text-xl">No products found.</p>;
  }

  const safePage = Math.min(page, totalPages);
  const shown = paginate ? products.slice((safePage - 1) * pageSize, safePage * pageSize) : products;
  const goto = (p) => {
    setPage(p);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={topRef}>
      <div className={gridCls} data-testid="product-grid">
        {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>

      {paginate && totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-12" data-testid="pagination">
          <button onClick={() => safePage > 1 && goto(safePage - 1)} disabled={safePage === 1} className="w-10 h-10 rounded-full border border-espresso/20 flex items-center justify-center text-espresso disabled:opacity-30 hover:border-champagne transition-colors" data-testid="pagination-prev" aria-label="Previous page">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {pageNumbers(safePage, totalPages).map((n, i) =>
            n === "..." ? (
              <span key={`e-${i}`} className="w-10 h-10 flex items-center justify-center text-espresso/40 text-sm">…</span>
            ) : (
              <button key={n} onClick={() => goto(n)} className={`w-10 h-10 rounded-full text-sm transition-colors ${n === safePage ? "bg-espresso text-ivory font-medium" : "text-espresso hover:bg-beige"}`} data-testid={`pagination-page-${n}`}>
                {n}
              </button>
            )
          )}
          <button onClick={() => safePage < totalPages && goto(safePage + 1)} disabled={safePage === totalPages} className="w-10 h-10 rounded-full border border-espresso/20 flex items-center justify-center text-espresso disabled:opacity-30 hover:border-champagne transition-colors" data-testid="pagination-next" aria-label="Next page">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
